import { useEffect, useState } from "react";
import { isNavigationAsset, type NavigationData } from "@cs2/physics";

/**
 * Lädt das Navigations-Asset einer Karte (Valve-Navmesh, Format v2) und hält
 * es im Prozess.
 *
 * Der Rest — Ebenenwahl, A*, Funnel — liegt in `@cs2/physics/navigation`.
 * Hier bleibt nur, was Anwendung ist: `fetch`, Zwischenspeicher und die
 * React-Anbindung. Ohne Asset zeichnet die Route gerade Linien.
 */
const navigationCache = new Map<string, Promise<NavigationData | null>>();

export function loadWalkabilityGrid(
  mapId: string,
): Promise<NavigationData | null> {
  let cached = navigationCache.get(mapId);
  if (!cached) {
    cached = (async (): Promise<NavigationData | null> => {
      try {
        const response = await fetch(`/assets/navigation/${mapId}.json`);
        if (!response.ok) return null;
        const data: unknown = await response.json();
        return isNavigationAsset(data) && data.mapId === mapId ? data : null;
      } catch {
        return null;
      }
    })();
    navigationCache.set(mapId, cached);
  }
  return cached;
}

export function useWalkabilityGrid(
  mapId: string | null,
): NavigationData | null {
  const [grid, setGrid] = useState<NavigationData | null>(null);
  useEffect(() => {
    let active = true;
    setGrid(null);
    if (!mapId) return;
    // `loadWalkabilityGrid` fängt intern alles ab und kann nicht rejecten —
    // daher bewusst nur `void` statt eines eigenen `catch`.
    void loadWalkabilityGrid(mapId).then((loaded) => {
      if (active) setGrid(loaded);
    });
    return () => {
      active = false;
    };
  }, [mapId]);
  return grid;
}
